// Vue
import Vue from "vue";
import Router from "vue-router";

// Layouts
import Landing from "@/components/Landing";
import Login from "@/components/admin/Login";
import ArticleAttendanceLayout from "@/components/admin/layout/ArticleAttendanceLayout";
import ContactLayout from "@/components/admin/layout/ContactLayout";
import Dashboard from "@/components/admin/layout/Dashboard";
import GreetingLayout from "@/components/admin/layout/GreetingLayout";
import HomecareLayout from "@/components/admin/layout/HomecareLayout";
import ReportLayout from "@/components/admin/layout/ReportLayout";
import StaffLayout from "@/components/admin/layout/StaffLayout";
import Content from "@/components/general/Content";
import Maintenance from "@/components/general/Maintenance";
import NotFound from "@/components/general/NotFound";
import Redirect from "@/components/general/Redirect";
import ConferenceLayout from "@/components/other-web/Conference/ConferenceLayout";
import OtherLayout from "@/components/other-web/OtherLayout";
import PolicyLayout from "@/components/policy/PolicyLayout";

// Pages
import AttendanceList from "@/components/admin/view/article/AttendanceList";
import GroupChat from "@/components/admin/view/chat/GroupChat";
import GroupManagement from "@/components/admin/view/chat/GroupManagement";
import PersonalSupportChat from "@/components/admin/view/chat/PersonalSupportChat";
import Contact from "@/components/admin/view/contact/Contact";
import ContactListNew from "@/components/admin/view/contact/ContactListNew";
import ContactProject from "@/components/admin/view/contact/ContactProjects";
import Greeting from "@/components/admin/view/greeting/Greeting";
import Homecare from "@/components/admin/view/homecare/Homecare";
import HomecareProject from "@/components/admin/view/homecare/HomecareProjects";
import ReportList from "@/components/admin/view/report/ReportList";
import Staff from "@/components/admin/view/staff/Staff";
import StaffCreate from "@/components/admin/view/staff/StaffCreate";
import StaffEdit from "@/components/admin/view/staff/StaffEdit";
import ConferenceBookingConfirm from "@/components/other-web/Conference/pages/ConferenceBookingConfirm";
import ConferenceBookingCreate from "@/components/other-web/Conference/pages/ConferenceBookingCreate";
import ConferenceBookingDetail from "@/components/other-web/Conference/pages/ConferenceBookingDetail";
import ConferenceBookingEditDetail from "@/components/other-web/Conference/pages/ConferenceBookingEditDetail";
import ConferenceDashboard from "@/components/other-web/Conference/pages/ConferenceDashboard";
import ConferenceSearchCustomerBooking from "@/components/other-web/Conference/pages/ConferenceSearchCustomerBooking";
import ConferenceSearchItem from "@/components/other-web/Conference/pages/ConferenceSearchItem";
import SupplierAppointmentList from "@/components/other-web/supplier/pages/AppointmentList";
import SupplierCreateInvoice from "@/components/other-web/supplier/pages/CreateInvoice";
import SupplierDashboard from "@/components/other-web/supplier/pages/Dashboard";
import SupplierInvoiceDashboard from "@/components/other-web/supplier/pages/InvoiceDashboard";
import SupplierInvoiceDetail from "@/components/other-web/supplier/pages/InvoiceDetail";
import SupplierJobDetail from "@/components/other-web/supplier/pages/JobDetail";
import SupplierJobList from "@/components/other-web/supplier/pages/JobList";
import SupplierLogin from "@/components/other-web/supplier/pages/Login";
import SupplierReportList from "@/components/other-web/supplier/pages/ReportList";
import Consent from "@/components/policy/Consent";
import Privacy from "@/components/policy/Privacy";
import Terms from "@/components/policy/Terms";
import CovidStatusPage from "@/components/third-party/CovidStatusPage";
import ForgotPassword from "@/components/user/ForgotPassword";

// Config
import Constant from "@/config/constant";

// Feature routes
import {
  articlePinnedRoutes,
  articlesRoutes,
  bannerPinnedRoutes,
  bannerRoutes,
  previewArticleRoutes
} from "../features/articles/routes";
import { authRoutes } from "../features/auth/routes";
import { cobeCommunityRoutes } from "../features/cobe-community/routes";
import { communicationRoutes } from "../features/communications/routes";
import { conferenceRoutes } from "../features/conferences/routes";
import { evRoutes } from "../features/ev-parking/routes";
import { feedbackRoutes } from "../features/feedbacks/routes";
import { financialInstitutionRoutes } from "../features/financial-institutions/routes";
import { groupRoutes } from "../features/group/routes";
import { homeAutomationRoutes } from "../features/home-automation/routes";
import { insuranceRoutes } from "../features/insurance/routes";
import { juristicFeatureRoutes } from "../features/juristic-feature/routes";
import { leadManagementRoutes } from "../features/lead-management/routes";
import { managementRoutes } from "../features/managements/routes";
import { monitoringRoutes } from "../features/monitoring/routes";
import { morningCoinRoutes } from "../features/morning-coin/routes";
import { NonScFeature } from "../features/nonsc/feature";
import { redeemedMNCRoutes } from '../features/redeemed-mnc/routes';
import { refinanceRoutes } from "../features/refinance/routes";
import { residentRoutes } from "../features/residents/routes";
import { ruejaiClubRoutes } from "../features/ruejai-club/routes";
import { sapRoutes } from "../features/sap/routes";
import { sellAndRentRoutes } from "../features/sell-and-rent/routes";
import { SubsReportFeature } from "../features/subscribers-report/feature";
import PackageExportRegistrationFormPage from "../features/subscriptions/modules/packages/pages/PackageExportRegistrationFormPage";
import { previewPackageRoutes } from "../features/subscriptions/modules/packages/routes";
import { subscriptionRoutes } from "../features/subscriptions/routes";

// Admin Guard
import { AdminGuard } from './admin-guard';

Vue.use(Router);

// vue-router rejects push()/replace() when the target is a redundant/aborted
// navigation. Most call sites in this app don't handle that rejection, which
// surfaces as an uncaught runtime error. Swallow known navigation failures
// globally instead of patching every call site individually.
["push", "replace"].forEach(method => {
  const original = Router.prototype[method];

  Router.prototype[method] = function patchedNavigate(location, onResolve, onReject) {
    if (onResolve || onReject) {
      return original.call(this, location, onResolve, onReject);
    }

    return original.call(this, location).catch(err => {
      if (Router.isNavigationFailure(err)) {
        return err;
      }

      return Promise.reject(err);
    });
  };
});

export default new Router({
  mode: "history",
  routes: [
    {
      path: "/",
      name: "Landing",
      component: Landing
    },
    {
      path: "/redirect-book-service",
      name: "RedirectBookService",
      component: Redirect
    },
    {
      path: "/third-party/covid19",
      name: "CovidStatus",
      component: CovidStatusPage
    },
    {
      path: "/content",
      name: "Content",
      component: Content
    },
    {
      path: "/maintenance",
      name: "Maintenance",
      component: Maintenance
    },
    {
      path: "/admins/packages/:id/registration-forms",
      name: "PackageExportRegistrationFormPage",
      component: PackageExportRegistrationFormPage
    },
    {
      path: "/admins/logins",
      name: "Login",
      component: Login
    },
    {
      path: "/admins/",
      name: "Dashboard",
      beforeEnter: AdminGuard.watch,
      component: Dashboard,
      children: [
        subscriptionRoutes,
        managementRoutes,
        communicationRoutes,
        homeAutomationRoutes,
        refinanceRoutes,
        insuranceRoutes,
        sellAndRentRoutes,
        financialInstitutionRoutes,
        monitoringRoutes,
        ruejaiClubRoutes,
        residentRoutes,
        groupRoutes,
        juristicFeatureRoutes,
        evRoutes,
        feedbackRoutes,
        leadManagementRoutes,
        {
          path: "ruejai-chat",
          redirect: (to) => ({
            name: "PersonalSupportChat",
            query: to.query
          })
        },
        {
          path: "personal-support-chat",
          component: OtherLayout,
          children: [
            {
              path: "/",
              name: "PersonalSupportChat",
              component: PersonalSupportChat
            }
          ]
        },
        {
          path: "group-chat",
          component: OtherLayout,
          children: [
            {
              path: "/",
              name: "GroupChat",
              component: GroupChat
            }
          ]
        },
        {
          path: "group-management",
          component: OtherLayout,
          children: [
            {
              path: ":pathMatch(.*)?",
              name: "GroupManagement",
              component: GroupManagement
            }
          ]
        },
        {
          path: "contacts",
          component: ContactLayout,
          meta: {
            breadcrumb: Constant.BREADCRUMB.PROJECT_ALL
          },
          children: [
            {
              path: "/",
              name: "ContactProject",
              component: ContactProject
            },
            {
              path: ":contactId",
              name: "Contact",
              component: Contact,
              meta: {
                breadcrumb: Constant.BREADCRUMB.PROJECT_SELECT
              }
            }
          ]
        },
        {
          path: "contacts-new",
          component: ContactLayout,
          meta: {
            breadcrumb: Constant.BREADCRUMB.PROJECT_ALL
          },
          children: [
            {
              path: "/",
              name: "ContactListNew",
              component: ContactListNew
            },
            {
              path: ":contactId",
              name: "ContactDetailNew",
              component: Contact,
              meta: {
                breadcrumb: Constant.BREADCRUMB.PROJECT_SELECT
              }
            }
          ]
        },
        previewArticleRoutes,
        previewPackageRoutes,
        bannerRoutes,
        bannerPinnedRoutes,
        articlePinnedRoutes,
        articlesRoutes,
        {
          path: "homecare",
          component: HomecareLayout,
          meta: {
            breadcrumb: Constant.BREADCRUMB.PROJECT_ALL
          },
          children: [
            {
              path: "/",
              name: "HomecareProject",
              component: HomecareProject
            },
            {
              path: ":projectId/jobs/:jobId",
              name: "Homecare",
              component: Homecare,
              meta: {
                breadcrumb: Constant.BREADCRUMB.PROJECT_SELECT
              }
            }
          ]
        },
        {
          path: "staffs",
          component: StaffLayout,
          meta: {
            breadcrumb: Constant.BREADCRUMB.ALL
          },
          children: [
            {
              path: "/",
              name: "Staffs",
              component: Staff,
              children: [
                {
                  path: "create",
                  name: "StaffCreate",
                  component: StaffCreate,
                  meta: {
                    breadcrumb: Constant.BREADCRUMB.CREATE
                  }
                },
                {
                  path: ":adminId",
                  name: "StaffEdit",
                  component: StaffEdit,
                  meta: {
                    breadcrumb: Constant.BREADCRUMB.EDIT
                  }
                }
              ]
            }
          ]
        },
        {
          path: "greeting",
          component: GreetingLayout,
          meta: { breadcrumb: Constant.BREADCRUMB.PROJECT_ALL },
          children: [
            {
              path: "/",
              name: "Greeting",
              component: Greeting
            },
            {
              path: ":userId",
              name: "GreetingView",
              component: Greeting
            }
          ]
        },
        {
          path: "reports",
          component: ReportLayout,
          children: [
            {
              path: "/",
              name: "Report",
              component: ReportList
            }
          ]
        },
        sapRoutes,
        morningCoinRoutes,
        conferenceRoutes,
        redeemedMNCRoutes,
        NonScFeature.routes,
        SubsReportFeature.routes,
        cobeCommunityRoutes
      ]
    },
    {
      path: "/admins/attendances/",
      component: ArticleAttendanceLayout,
      children: [
        {
          path: ":articleType/:articleId",
          name: "AttendanceList",
          component: AttendanceList
        }
      ]
    },
    {
      path: "/password/reset",
      name: "ForgotPassword",
      component: ForgotPassword
    },
    {
      path: "/terms",
      name: "Terms",
      component: Terms
    },
    {
      path: "/policy",
      name: "Policy",
      component: Privacy
    },
    {
      path: "/policy",
      component: PolicyLayout,
      children: [
        {
          name: "PdpaConsent",
          path: "PDPA/consent",
          component: Consent
        },
        {
          name: "Privacy",
          path: "privacy",
          component: Privacy
        },
        {
          name: "PolicyTerms",
          path: "terms",
          component: Terms
        }
      ]
    },
    {
      path: "*",
      component: NotFound
    },
    {
      path: "/",
      name: "Others",
      component: OtherLayout,
      children: [
        {
          path: "/conference",
          name: "Conference",
          component: ConferenceLayout,
          children: [
            {
              path: "dashboard",
              name: "DashboardConference",
              component: ConferenceDashboard
            },
            {
              path: "booking/create/:conferenceId",
              name: "BookingCreate",
              component: ConferenceBookingCreate
            },
            {
              path: "booking/confirm/:conferenceId",
              name: "BookingConfirm",
              component: ConferenceBookingConfirm
            },
            {
              path: "search",
              name: "ConferenceSearchItem",
              component: ConferenceSearchItem
            },
            {
              path: "booking/:bookingId",
              name: "ConferenceBookingDetail",
              component: ConferenceBookingDetail
            },
            {
              path: "search/customer",
              name: "ConferenceSearchCustomerBooking",
              component: ConferenceSearchCustomerBooking
            },
            {
              path: "booking/detail/:bookingId",
              name: "ConferenceBookingEditDetail",
              component: ConferenceBookingEditDetail
            }
          ]
        }
      ]
    },
    {
      path: "/partners/logins",
      name: "SupplierLogin",
      component: SupplierLogin
    },
    {
      path: "/partners",
      name: "SupplierDashboard",
      component: SupplierDashboard,
      children: [
        {
          path: "jobs/:navigationMenu",
          name: "SupplierJobList",
          component: SupplierJobList
        },
        {
          path: "jobs/:navigationMenu/details/:appointmentId",
          name: "SupplierJobDetail",
          component: SupplierJobDetail
        },
        {
          path: "jobs/:navigationMenu/details/:appointmentId/appointments",
          name: "SupplierAppointmentList",
          component: SupplierAppointmentList
        },
        {
          path: "report/jobs",
          name: "SupplierReportList",
          component: SupplierReportList
        },
        {
          path: "invoice",
          name: "SupplierInvoiceDashboard",
          component: SupplierInvoiceDashboard
        },
        {
          path: "invoice/create",
          name: "SupplierCreateInvoice",
          component: SupplierCreateInvoice
        },
        {
          path: "invoice/detail/:invoiceId",
          name: "SupplierInvoiceDetail",
          component: SupplierInvoiceDetail
        }
      ],
    },
    authRoutes,
  ]
});
